import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { simulationService } from '~/services';
import { queryCreator, arrayToMap } from '~/hooks/useQueryCreator';
import { useSession } from '~/hooks/useSession';
import {
  enrichWithFileUploadDetail,
  getFileUploadId,
  needsFileUploadDetail,
  type UnifiedSimulationUpload,
} from '~/components/simulation/shared/uploadAdapter';
import type { SimulationUpload } from '~/store/simulation/types';
import type { SimulationServiceV1SimulationFileUploadsDetailData } from '@shipium-corp/shipium-ts-types';

const SELECTION_DETAIL_QUERY_KEY = 'simulation-service/file-uploads/selection-detail';

type SelectionDetail = {
  byId: Record<string, SimulationServiceV1SimulationFileUploadsDetailData>;
  failedFileIds: string[];
};

/**
 * Re-derives the per-origin / per-fulfillment-context breakdown that toPersistableSelection keeps
 * out of simulationSetupData, so the mapping step can build its records from a light persisted
 * selection. Legacy uploads, and selections saved before that change, carry it inline already and
 * are passed straight through.
 *
 * A file that cannot be read is reported in `failedFileIds` rather than failing the whole
 * selection: the caller decides what to do about it, and keeps the breakdown of every file that
 * did resolve.
 */
export function useSelectedFileDetails(selectedFiles: SimulationUpload[]): {
  files: SimulationUpload[];
  isLoading: boolean;
  isError: boolean;
  failedFileIds: string[];
  retry: () => void;
} {
  const { sessionPartnerUrl } = useSession();
  const queryClient = useQueryClient();

  const missingDetailIds = useMemo(
    () =>
      (selectedFiles as UnifiedSimulationUpload[])
        .filter(needsFileUploadDetail)
        .map((upload) => getFileUploadId(upload) as string)
        .sort(),
    [selectedFiles],
  );

  // One query for the whole selection (keyed on its ids) rather than one per file, so `data` keeps a
  // stable identity for the downstream record memos — re-running them means re-enriching ~800 origins
  // per file. The per-id fetches still run through the shared cache the file picker already warms.
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [sessionPartnerUrl, SELECTION_DETAIL_QUERY_KEY, ...missingDetailIds],
    queryFn: async (): Promise<SelectionDetail> => {
      // allSettled, not all: an upload that cannot be read (a deleted one 404s for good) must not
      // discard the breakdown of every other file in the selection.
      const settled = await Promise.allSettled(
        missingDetailIds.map((fileUploadId) =>
          queryClient.fetchQuery(
            queryCreator(sessionPartnerUrl, simulationService.fileUploads.get, { args: [fileUploadId] }),
          ),
        ),
      );
      const byId = arrayToMap(
        settled.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : [])),
        'id',
      );
      // Keyed off what actually landed in the map, not off which promises rejected: a response
      // that does not come back under the id it was asked for leaves that file just as unread.
      return { byId, failedFileIds: missingDetailIds.filter((fileUploadId) => !byId[fileUploadId]) };
    },
    enabled: missingDetailIds.length > 0,
    // A processed upload's breakdown never changes, so don't re-derive it on focus or remount.
    staleTime: Infinity,
    cacheTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });

  const files = useMemo(
    () =>
      data
        ? (selectedFiles as UnifiedSimulationUpload[]).map((upload) => {
            const detail = data.byId[getFileUploadId(upload) ?? ''];
            return detail ? enrichWithFileUploadDetail(upload, detail) : upload;
          })
        : selectedFiles,
    [selectedFiles, data],
  );

  // Drop the per-file entries first: refetching the selection re-reads each file through the shared
  // cache, so a retry that leaves the failed ones cached would re-run against the same failure.
  const retry = useCallback(() => {
    (data?.failedFileIds ?? []).forEach((fileUploadId) =>
      queryClient.removeQueries({
        queryKey: [sessionPartnerUrl, simulationService.fileUploads.get.queryKey, fileUploadId],
      }),
    );
    void refetch();
  }, [data, queryClient, sessionPartnerUrl, refetch]);

  // react-query reports a disabled query as loading; only a real fetch should gate the step.
  return {
    files,
    isLoading: missingDetailIds.length > 0 && isLoading,
    isError,
    failedFileIds: data?.failedFileIds ?? [],
    retry,
  };
}
